import React from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import {
  Tag, Trophy, Users, Activity, HeartPulse,
  CreditCard, BarChart2, UserCog, Settings, ArrowRight,
} from "lucide-react";

interface ModuleCard {
  title: string;
  desc: string;
  path: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  roles: string[];
}

const MODULES: ModuleCard[] = [
  { title: "Categorías",   desc: "Administra divisiones y grupos del plantel",
    path: "/categorias", icon: <Tag size={22}/>,        color: "text-violet-600", bg: "bg-violet-50",
    roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista"] },
  { title: "Torneos",      desc: "Competencias, tablas y calendarios",
    path: "/torneos",    icon: <Trophy size={22}/>,      color: "text-amber-600",  bg: "bg-amber-50",
    roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
  { title: "Jugadores",    desc: "Perfiles, estadísticas y dossiers PDF",
    path: "/jugadores",  icon: <Users size={22}/>,       color: "text-blue-600",   bg: "bg-blue-50",
    roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista"] },
  { title: "Partidos",     desc: "Registro de resultados y estadísticas",
    path: "/partidos",   icon: <Activity size={22}/>,    color: "text-green-600",  bg: "bg-green-50",
    roles: ["admin","dt","analista","jugador","padre"] },
  { title: "Wellness",     desc: "Bienestar, fatiga y riesgo de lesión",
    path: "/wellness",   icon: <HeartPulse size={22}/>,  color: "text-rose-600",   bg: "bg-rose-50",
    roles: ["admin","dt","pf","fisio"] },
  { title: "Pagos",        desc: "Mensualidades, cartera y recordatorios",
    path: "/pagos",      icon: <CreditCard size={22}/>,  color: "text-emerald-600",bg: "bg-emerald-50",
    roles: ["admin"] },
  { title: "Analytics",   desc: "Informes de rendimiento con IA",
    path: "/analytics",  icon: <BarChart2 size={22}/>,   color: "text-cyan-600",   bg: "bg-cyan-50",
    roles: ["admin","dt","analista"] },
  { title: "Staff",        desc: "Entrenadores, fisios y cuerpo técnico",
    path: "/staff",      icon: <UserCog size={22}/>,     color: "text-indigo-600", bg: "bg-indigo-50",
    roles: ["admin"] },
  { title: "Configuración",desc: "Perfil, contraseña y usuarios",
    path: "/settings",   icon: <Settings size={22}/>,    color: "text-gray-600",   bg: "bg-gray-100",
    roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
];

const ROL_BIENVENIDA: Record<string,string> = {
  admin: "Tienes acceso completo al sistema.",
  dt: "Visualiza el rendimiento de tu categoría.",
  pf: "Monitorea la condición física del plantel.",
  fisio: "Revisa el estado físico y wellness.",
  nutricion: "Gestiona el estado nutricional.",
  psicologo: "Sigue el bienestar emocional.",
  analista: "Analiza estadísticas y tendencias.",
  jugador: "Consulta tu perfil y estadísticas.",
  padre: "Sigue el progreso de tu hijo/a.",
};

export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  const nav = useNavigate();
  const userRol = (user?.rol || "").toLowerCase();
  const visibleModules = MODULES.filter(m => m.roles.some(r => r === userRol));

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Bienvenido, {user?.nombre}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">{ROL_BIENVENIDA[userRol] || "Sistema de gestión deportiva."}</p>
        </div>

        {/* Módulos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleModules.map(m => (
            <button
              key={m.path}
              onClick={() => nav(m.path)}
              className="group text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md hover:shadow-blue-500/10 transition-all duration-200"
            >
              <div className="flex items-start justify-between">
                <div className={`w-11 h-11 rounded-xl ${m.bg} ${m.color} flex items-center justify-center flex-shrink-0`}>
                  {m.icon}
                </div>
                <ArrowRight size={16} className="text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all mt-1"/>
              </div>
              <div className="mt-4">
                <h3 className="font-semibold text-gray-900 text-sm">{m.title}</h3>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed">{m.desc}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Quick stats para admin */}
        {hasRole("admin") && (
          <div className="mt-8 bg-gradient-to-br from-[#0D1B2A] to-[#1C3050] rounded-2xl p-6 text-white">
            <h2 className="font-semibold text-sm text-white/70 uppercase tracking-wide mb-4">Acceso rápido</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Ver manual", action: () => window.open("/manual.html", "_blank"), icon: "📖" },
                { label: "Crear categoría", action: () => nav("/categorias"), icon: "➕" },
                { label: "Nuevo torneo", action: () => nav("/torneos"), icon: "🏆" },
                { label: "Gestionar staff", action: () => nav("/staff"), icon: "👥" },
              ].map(item => (
                <button key={item.label} onClick={item.action}
                  className="bg-white/10 hover:bg-white/20 rounded-xl p-4 text-left transition-colors">
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <div className="text-sm font-medium">{item.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
